import { FormControlLabel, Switch } from "@mui/material";
import React, {
  ChangeEvent,
  ComponentProps,
  FC,
  ReactNode,
  useCallback,
} from "react";
import styled from "styled-components";
import { PunctThemeProps, styledColor } from "../../styles/theme";

export type SwitchColorScheme = "default" | "green-red";

type StyledSwitchInputProps = {
  colorScheme?: SwitchColorScheme;
  intermediate?: boolean;
  value: boolean;
};

const getSwitchColor = (props: PunctThemeProps<StyledSwitchInputProps>) => {
  if (props.colorScheme === "default") return;
  const color = props.intermediate
    ? styledColor("grey")(props)
    : props.value
    ? styledColor("green")(props)
    : styledColor("red")(props);
  return `${color} !important`;
};

const StyledSwitchInput = styled.div`
  color: ${getSwitchColor};

  .MuiButtonBase-root {
    color: ${getSwitchColor};
  }

  .MuiSwitch-track {
    background-color: ${getSwitchColor};
  }

  .SwitchInput__label {
    margin-left: 0;
    flex-direction: row-reverse;
  }
`;

export type SwitchInputProps = {
  children?: ReactNode;
  className?: string;
  value: boolean;
  onChange: (newValue: boolean) => void;
  name?: string;
  colorScheme?: SwitchColorScheme;
  intermediate?: boolean;
} & Pick<ComponentProps<typeof Switch>, "disabled" | "aria-label">;

const SwitchInput: FC<SwitchInputProps> = ({
  colorScheme = "default",
  className,
  value,
  onChange,
  children,
  name,
  intermediate,
  ...switchProps
}) => {
  const handleChange = useCallback(
    (event: ChangeEvent, checked: boolean) => onChange(checked),
    [onChange],
  );

  return (
    <StyledSwitchInput
      value={value}
      colorScheme={colorScheme}
      className={className}
      intermediate={intermediate}
    >
      <FormControlLabel
        className="SwitchInput__label"
        control={
          <Switch
            checked={value}
            onChange={handleChange}
            name={name}
            {...switchProps}
          />
        }
        label={children}
      />
    </StyledSwitchInput>
  );
};

export { SwitchInput };
